/**
 * A viewcube component that provides 3D navigation controls for a viewport
 * This component displays a cube that shows the current view orientation
 * and allows users to click on faces to change the view
 */

import styleText from './as-viewcube-v1.css?raw';

interface CameraParams {
  right: number[];
  top: number[];
  view: number[];
  focus: number[];
  origin: number[];
  aperture: number;
}

interface Viewport {
  getCameraParams: () => CameraParams;
  setCamera: (params: CameraParams, animate?: boolean) => void;
  moveCamera: (params: CameraParams, callback?: () => void, speed?: number, frames?: number) => void;
  autoPosCamera: (animate?: boolean) => void;
  Dist3: (a: number[], b: number[]) => number;
  RotMatrix44: (axis: number[], angle: number) => number[];
  Mat44Vec3: (matrix: number[], vector: number[]) => number[];
}

interface ViewportEvent extends Event {
  detail: {
    viewport: Viewport;
  };
}

// Constants
const PERSPECTIVE_VIEW = true;
const APERTURE_VAL = 35.0;
const DISTANCE_FACTOR = 35000.0;

// Types for view positions
type ViewPosition = 
  | 'front' | 'back' | 'top' | 'bottom' | 'left' | 'right'
  | 'top_front_right' | 'top_front_left' | 'top_back_right' | 'top_back_left'
  | 'bottom_back_right' | 'bottom_front_right' | 'bottom_front_left' | 'bottom_back_left';

var perspectiveView = true;
var apertureVal = 35.0;
var distanceFactor = 35000.0;
var hasShadow = true;

var lastView;


function togglePerspectiveView(viewport: Viewport, e?: MouseEvent): void {
  if (e) {
    e.stopPropagation();
    const target = e.target as HTMLElement;
    target.classList.toggle('selected');
  } else {
    const perspectiveBtn = document.getElementById('perspective-btn');
    if (perspectiveBtn) {
      perspectiveBtn.classList.remove('selected');
    }
  }

  const ruler = document.getElementById('viewPort-ruler');
  if (ruler) {
    ruler.classList.toggle('inactive');
  }

  perspectiveView = !perspectiveView;
  apertureVal = perspectiveView ? 35.0 : 0.001;
  
  const view = viewport.getCameraParams();
  for (let i = 0; i < view.focus.length; i++) {
    const f = view.focus[i] - view.origin[i];
    view.focus[i] = (!perspectiveView) ? view.origin[i] + f * DISTANCE_FACTOR : view.origin[i] + f / DISTANCE_FACTOR;
  }

  view.aperture = apertureVal;
  viewport.setCamera(view, true);
}

function generateViewParams(
  origin: number[],
  distance: number,
  right: number[],
  top: number[],
  view: number[]
): CameraParams {
  return {
    aperture: APERTURE_VAL,
    focus: [
      origin[0] + distance * view[0],
      origin[1] + distance * view[1],
      origin[2] + distance * view[2]
    ],
    origin,
    right,
    top,
    view
  };
}

function generateViews(camParams: CameraParams, distanceCamOrigin: number): Record<ViewPosition, CameraParams> {
  const { origin } = camParams;
  
  // Base vectors for different orientations
  const vectors = {
    front: {
      right: [-1, 0, 0],
      top: [0, 0, 1],
      view: [0, -1, 0]
    },
    back: {
      right: [1, 0, 0],
      top: [0, 0, 1],
      view: [0, 1, 0]
    },
    top: {
      right: [1, 0, 0],
      top: [0, 1, 0],
      view: [0, 0, -1]
    },
    bottom: {
      right: [1, 0, 0],
      top: [0, -1, 0],
      view: [0, 0, 1]
    },
    left: {
      right: [0, 1, 0],
      top: [0, 0, 1],
      view: [-1, 0, 0]
    },
    right: {
      right: [0, -1, 0],
      top: [0, 0, 1],
      view: [1, 0, 0]
    }
  };

  // Corner vectors
  const cornerVectors = {
    top_front_right: {
      right: [1 / Math.sqrt(2), 1 / Math.sqrt(2), 0],
      top: [-1 / Math.sqrt(6), 1 / Math.sqrt(6), 2 / Math.sqrt(6)],
      view: [-1 / Math.sqrt(3), 1 / Math.sqrt(3), -1 / Math.sqrt(3)]
    },
    top_front_left: {
      right: [-1 / Math.sqrt(2), 1 / Math.sqrt(2), 0],
      top: [1 / Math.sqrt(6), 1 / Math.sqrt(6), 2 / Math.sqrt(6)],
      view: [1 / Math.sqrt(3), 1 / Math.sqrt(3), -1 / Math.sqrt(3)]
    },
    top_back_right: {
      right: [1 / Math.sqrt(2), -1 / Math.sqrt(2), 0],
      top: [-1 / Math.sqrt(6), -1 / Math.sqrt(6), 2 / Math.sqrt(6)],
      view: [1 / Math.sqrt(3), -1 / Math.sqrt(3), -1 / Math.sqrt(3)]
    },
    top_back_left: {
      right: [-1 / Math.sqrt(2), -1 / Math.sqrt(2), 0],
      top: [1 / Math.sqrt(6), -1 / Math.sqrt(6), 2 / Math.sqrt(6)],
      view: [-1 / Math.sqrt(3), -1 / Math.sqrt(3), -1 / Math.sqrt(3)]
    },
    bottom_back_right: {
      right: [1 / Math.sqrt(2), -1 / Math.sqrt(2), 0],
      top: [-1 / Math.sqrt(6), -1 / Math.sqrt(6), -2 / Math.sqrt(6)],
      view: [1 / Math.sqrt(3), -1 / Math.sqrt(3), 1 / Math.sqrt(3)]
    },
    bottom_front_right: {
      right: [1 / Math.sqrt(2), 1 / Math.sqrt(2), 0],
      top: [-1 / Math.sqrt(6), 1 / Math.sqrt(6), -2 / Math.sqrt(6)],
      view: [-1 / Math.sqrt(3), 1 / Math.sqrt(3), 1 / Math.sqrt(3)]
    },
    bottom_front_left: {
      right: [-1 / Math.sqrt(2), 1 / Math.sqrt(2), 0],
      top: [1 / Math.sqrt(6), 1 / Math.sqrt(6), -2 / Math.sqrt(6)],
      view: [1 / Math.sqrt(3), 1 / Math.sqrt(3), 1 / Math.sqrt(3)]
    },
    bottom_back_left: {
      right: [-1 / Math.sqrt(2), -1 / Math.sqrt(2), 0],
      top: [1 / Math.sqrt(6), -1 / Math.sqrt(6), -2 / Math.sqrt(6)],
      view: [-1 / Math.sqrt(3), -1 / Math.sqrt(3), 1 / Math.sqrt(3)]
    }
  };

  // Generate views for all positions
  const views: Record<ViewPosition, CameraParams> = {} as Record<ViewPosition, CameraParams>;

  // Generate basic views
  Object.entries(vectors).forEach(([key, vectors]) => {
    views[key as ViewPosition] = generateViewParams(
      origin,
      distanceCamOrigin,
      vectors.right,
      vectors.top,
      vectors.view
    );
  });

  // Generate corner views
  Object.entries(cornerVectors).forEach(([key, vectors]) => {
    views[key as ViewPosition] = generateViewParams(
      origin,
      distanceCamOrigin,
      vectors.right,
      vectors.top,
      vectors.view
    );
  });

  return views;
}

function toggleDefaultViews(viewport: Viewport, view: ViewPosition): void {
  const mirroredViews: Record<ViewPosition, ViewPosition> = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
    front: 'back',
    back: 'front',
    top_front_right: 'bottom_back_left',
    top_front_left: 'bottom_back_right',
    top_back_right: 'bottom_front_left',
    top_back_left: 'bottom_front_right',
    bottom_back_right: 'top_front_left',
    bottom_front_right: 'top_back_left',
    bottom_front_left: 'top_back_right',
    bottom_back_left: 'top_front_right'
  };

  const camParams = viewport.getCameraParams();
  const distanceCamOrigin = viewport.Dist3(camParams.origin, camParams.focus);
  const views = generateViews(camParams, distanceCamOrigin);

  if (view === lastView) {
      viewport.moveCamera(views[mirroredViews[lastView]], () => {
      viewport.autoPosCamera(true);
      lastView = mirroredViews[view];
    }, 0, 1);
  } else {
    viewport.moveCamera(views[view], () => {
      viewport.autoPosCamera(true);
      lastView = view;
    }, 0, 0);
  }
}

function setView(viewport: Viewport, _view: ViewPosition, speed = 400, frames = 30): void {
  const cam = viewport.getCameraParams();
  const dist = viewport.Dist3(cam.origin, cam.focus);

  let r: number[], t: number[], v: number[];
  switch (_view) {
    // Sides
    case 'front':
      r = [1, 0, 0];
      t = [0, 0, 1];
      v = [0, 1, 0];
      break;
    case 'back':
      r = [-1, 0, 0];
      t = [0, 0, 1];
      v = [0, -1, 0];
      break;
    case 'top':
      r = [1, 0, 0];
      t = [0, 1, 0];
      v = [0, 0, -1];
      break;
    case 'bottom':
      r = [1, 0, 0];
      t = [0, -1, 0];
      v = [0, 0, 1];
      break;
    case 'left':
      r = [0, 1, 0];
      t = [0, 0, 1];
      v = [-1, 0, 0];
      break;
    case 'right':
      r = [0, -1, 0];
      t = [0, 0, 1];
      v = [1, 0, 0];
      break;
    // Corners
    case 'top_front_right':
      r = [1 / Math.sqrt(2), 1 / Math.sqrt(2), 0];
      t = [-1 / Math.sqrt(6), 1 / Math.sqrt(6), 1 / Math.sqrt(6) * 2];
      v = [-1 / Math.sqrt(3), 1 / Math.sqrt(3), -1 / Math.sqrt(3)];
      break;
    case 'top_front_left':
      r = [1 / Math.sqrt(2), -1 / Math.sqrt(2), 0];
      t = [1 / Math.sqrt(6), 1 / Math.sqrt(6), 1 / Math.sqrt(6) * 2];
      v = [1 / Math.sqrt(3), 1 / Math.sqrt(3), -1 / Math.sqrt(3)];
      break;
    case 'top_back_right':
      r = [-1 / Math.sqrt(2), -1 / Math.sqrt(2), 0];
      t = [1 / Math.sqrt(6), -1 / Math.sqrt(6), 1 / Math.sqrt(6) * 2];
      v = [1 / Math.sqrt(3), -1 / Math.sqrt(3), -1 / Math.sqrt(3)];
      break;
    case 'top_back_left':
      r = [-1 / Math.sqrt(2), 1 / Math.sqrt(2), 0];
      t = [-1 / Math.sqrt(6), -1 / Math.sqrt(6), 1 / Math.sqrt(6) * 2];
      v = [-1 / Math.sqrt(3), -1 / Math.sqrt(3), -1 / Math.sqrt(3)];
      break;
    case 'bottom_back_right':
      r = [-1 / Math.sqrt(2), 1 / Math.sqrt(2), 0];
      t = [1 / Math.sqrt(6), 1 / Math.sqrt(6), 1 / Math.sqrt(6) * 2];
      v = [-1 / Math.sqrt(3), -1 / Math.sqrt(3), 1 / Math.sqrt(3)];
      break;
    case 'bottom_front_right':
      r = [1 / Math.sqrt(2), 1 / Math.sqrt(2), 0];
      t = [1 / Math.sqrt(6), -1 / Math.sqrt(6), 1 / Math.sqrt(6) * 2];
      v = [-1 / Math.sqrt(3), 1 / Math.sqrt(3), 1 / Math.sqrt(3)];
      break;
    case 'bottom_front_left':
      r = [1 / Math.sqrt(2), -1 / Math.sqrt(2), 0];
      t = [-1 / Math.sqrt(6), -1 / Math.sqrt(6), 1 / Math.sqrt(6) * 2];
      v = [1 / Math.sqrt(3), 1 / Math.sqrt(3), 1 / Math.sqrt(3)];
      break;
    case 'bottom_back_left':
      r = [-1 / Math.sqrt(2), -1 / Math.sqrt(2), 0];
      t = [-1 / Math.sqrt(6), 1 / Math.sqrt(6), 1 / Math.sqrt(6) * 2];
      v = [1 / Math.sqrt(3), -1 / Math.sqrt(3), 1 / Math.sqrt(3)];
      break;
  }

  const ap = APERTURE_VAL;
  const o = cam.origin;
  const of = [o[0] - (dist) * v[0], o[1] - (dist) * v[1], o[2] - (dist) * v[2]];
  const view: CameraParams = { aperture: ap, focus: of, origin: o, right: r, top: t, view: v };
  
  viewport.moveCamera(view, () => {
    viewport.autoPosCamera(true);
  }, speed, frames);
}

function rotateAroundXAxis(a: number): number[] {
  return [
    1, 0, 0, 0,
    0, Math.cos(a), -Math.sin(a), 0,
    0, Math.sin(a), Math.cos(a), 0,
    0, 0, 0, 1
  ];
}

function cameraUtilsUpdate(viewport: Viewport, cube: HTMLElement): void {
  var _a;
  const cam = viewport.getCameraParams();
  const mat = new DOMMatrix([
    -cam.right[0],
    -cam.right[1],
    -cam.right[2],
    0,
    cam.top[0],
    cam.top[1],
    cam.top[2],
    0,
    cam.view[0],
    cam.view[1],
    cam.view[2],
    0,
    0,
    0,
    0,
    1
  ]);
  const values1 = [
    mat.m11,
    mat.m12,
    mat.m13,
    mat.m14,
    mat.m21,
    mat.m22,
    mat.m23,
    mat.m24,
    mat.m31,
    mat.m32,
    mat.m33,
    mat.m34,
    mat.m41,
    mat.m42,
    mat.m43,
    mat.m44
  ];
  const joinedMat = `matrix3d(${values1.join(",")})`;
  const dots = (_a = cube.parentElement) == null ? void 0 : _a.querySelectorAll(".dot");
  dots == null ? void 0 : dots.forEach((dot) => {
    dot.style.transform = joinedMat;
  });
  mat.invertSelf();
  const values = [
    mat.m11,
    mat.m12,
    mat.m13,
    mat.m14,
    mat.m21,
    mat.m22,
    mat.m23,
    mat.m24,
    mat.m31,
    mat.m32,
    mat.m33,
    mat.m34,
    mat.m41,
    mat.m42,
    mat.m43,
    mat.m44
  ];
  cube.style.transform = `matrix3d(${values.join(",")})`;


}

function cubeElemHover(elem: HTMLElement, mode: boolean): void {
  const baseColor = elem.style.getPropertyValue('--base-color');
  const hoverColor = baseColor.replace(/[\d.]+\)$/, '0.5)');
  elem.style.setProperty('--hover-color', hoverColor);
}

export class AsViewCubeV1 extends HTMLElement {
  private viewport: Viewport | undefined;
  private lastView: ViewPosition | undefined;
  private shadow: ShadowRoot;
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'open' });
    this.shadow.adoptedStyleSheets = [
      ...this.shadow.adoptedStyleSheets,
      AsViewCubeV1.sheet
    ];
    this.shadow.innerHTML = /*html*/`
      <div id="cube-wrapper">
        <div id="cube-container">
          <div id="nav-cube">
            <!-- Front face -->
            <div class="cube-side front" id="front">
              <div class="axis"></div>
              <div class="axis"></div>
            </div>
            
            <!-- Top face -->
            <div class="cube-side top" id="top">
              <div class="axis"></div>
              <div class="axis"></div>
            </div>
            
            <!-- Right face -->
            <div class="cube-side right" id="right">
              <div class="axis"></div>
              <div class="axis"></div>
            </div>

            <!-- Corner dots -->
            <!-- Bottom face corners -->
            <div class="dot-container dot-bottom-front-right">
              <div class="dot" data-view="bottom_front_right"></div>
            </div>
            <div class="dot-container dot-bottom-front-left">
              <div class="dot" data-view="bottom_front_left"></div>
            </div>
            <div class="dot-container dot-bottom-back-left">
              <div class="dot" data-view="bottom_back_left"></div>
            </div>
            <div class="dot-container dot-bottom-back-right">
              <div class="dot" data-view="bottom_back_right"></div>
            </div>
            
            <!-- Top face corners -->
            <div class="dot-container dot-top-front-right">
              <div class="dot" data-view="top_front_right"></div>
            </div>
            <div class="dot-container dot-top-front-left">
              <div class="dot" data-view="top_front_left"></div>
            </div>
            <div class="dot-container dot-top-back-left">
              <div class="dot" data-view="top_back_left"></div>
            </div>
            <div class="dot-container dot-top-back-right">
              <div class="dot" data-view="top_back_right"></div>
            </div>
          </div>
        </div>
      </div>
    `;

    this.setupEventListeners();

    this.dispatchEvent(new CustomEvent('get-viewport', {
      bubbles: true,
      composed: true,
      detail: { requester: this }
    }));

    window.addEventListener('as-viewport-camera-update', () => {
      const cube = this.shadow.querySelector('#nav-cube');
      if (cube && this.viewport) {
        cameraUtilsUpdate(this.viewport, cube as HTMLElement);
      }
    });

    window.addEventListener('as-viewport-ready', (ev: ViewportEvent) => {
      this.viewport = ev.detail.viewport;
    });
  }

  private setupEventListeners(): void {
    // Setup cube side event listeners
    const cubeSides = this.shadow.querySelectorAll('.cube-side');
    cubeSides.forEach((side) => {
      const sideElement = side as HTMLElement;
      const sideType = sideElement.id as ViewPosition;
      
      // Set up hover effects
      cubeElemHover(sideElement, false);
      sideElement.onmouseenter = () => cubeElemHover(sideElement, true);
      sideElement.onmouseleave = () => cubeElemHover(sideElement, false);
      
      // Set up click handlers
      sideElement.onclick = () => {
        if (!this.viewport) return;
        
        const cam = this.viewport.getCameraParams();
        switch (sideType) {
          case 'right': 
            (cam.focus[0] < cam.origin[0]) ? setView(this.viewport, 'left') : setView(this.viewport, 'right'); 
            break;
          case 'front': 
            (cam.focus[1] > cam.origin[1]) ? setView(this.viewport, 'front') : setView(this.viewport, 'back'); 
            break;
          case 'top': 
            (cam.focus[2] < cam.origin[2]) ? setView(this.viewport, 'top') : setView(this.viewport, 'bottom'); 
            break;
        }
      };
    });

    // Setup dot event listeners
    const dots = this.shadow.querySelectorAll('.dot');
    dots.forEach((dot) => {
      const dotElement = dot as HTMLElement;
      const view = dotElement.getAttribute('data-view') as ViewPosition;
      
      // Set up hover effects
      cubeElemHover(dotElement, false);
      dotElement.onmouseenter = () => cubeElemHover(dotElement, true);
      dotElement.onmouseleave = () => cubeElemHover(dotElement, false);
      
      // Set up click handlers
      dotElement.onclick = () => {
        if (this.viewport && view) {
          setView(this.viewport, view);
        }
      };
    });
  }
}

customElements.define('as-viewcube-v1', AsViewCubeV1);
